import { Button } from '@/components/ui/button';
import { Loader2 } from 'lucide-react';

interface FormActionsProps {
  onCancel: () => void;
  onSave: () => void;
  hasChanges: boolean;
  saveLabel?: string;
  cancelLabel?: string;
  disabled?: boolean;
  isSubmitting?: boolean;
  onDelete?: () => void;
  /** Defaults to "Delete" — pass a more specific label where "Delete" alone
   *  would be ambiguous (e.g. "Delete contact"). */
  deleteLabel?: string;
  hideCancelButton?: boolean;
}

/**
 * Reusable form action buttons for Cancel/Save
 * Consistent styling and behavior across all inventory forms
 * Optional Delete button on the left side
 */
export function FormActions({
  onCancel,
  onSave,
  hasChanges: _hasChanges,
  saveLabel = 'Save Changes',
  cancelLabel = 'Cancel',
  disabled = false,
  isSubmitting = false,
  onDelete,
  deleteLabel = 'Delete',
  hideCancelButton = false
}: FormActionsProps) {
  return (
    <div className="flex w-full items-center pt-2">
      {/* Delete button on the left — ghost + destructive text, matching the
          entitlement drawer's "Delete entitlement" footer button
          (modal-footer.tsx) rather than an icon-only "bin" (LF-5547). */}
      {onDelete && (
        <Button
          tone="destructive"
          variant="ghost"
          onClick={onDelete}
          disabled={isSubmitting}
        >
          {deleteLabel}
        </Button>
      )}

      {/* Spacer to push buttons to the right */}
      <div className="flex-1" />

      {/* Cancel and Save buttons on the right */}
      <div className="flex gap-2">
        {!hideCancelButton && (
          <Button
            size="xs"
            density="compact"
            variant="outline"
            onClick={onCancel}
            disabled={isSubmitting}
          >
            {cancelLabel}
          </Button>
        )}
        <Button
          size="xs"
          density="compact"
          gap="standard"
          onClick={onSave}
          disabled={disabled || isSubmitting}
        >
          {isSubmitting && <Loader2 className="h-3.5 w-3.5 animate-spin" />}
          {isSubmitting ? 'Saving...' : saveLabel}
        </Button>
      </div>
    </div>
  );
}
